import { defaultNS, useTranslation } from "@/lib/i18n";
import { useMemo } from "react";
import type { Attribute, FieldValueType } from "../../../types";
import { getAttributeOptions } from "../../../utils/option-utils";
import { extractValueFromValueLabel } from "../../../utils/value-label-utils";
import type { Option } from "./DynamicSelectField.type";

/**
 * 前の値（previousValue）に対応するラベルを取得するカスタムフック
 */
export const usePreviousValueLabel = (
  previousValue: FieldValueType | undefined,
  field: Attribute,
  dynamicOptions: Option[],
): string => {
  const { t } = useTranslation(defaultNS);
  const fallbackText = t("common.unregistered");

  const staticOptions = useMemo(() => getAttributeOptions(field), [field]);

  return useMemo(() => {
    // previousValueから値部分を抽出
    const value = extractValueFromValueLabel(previousValue);
    if (!value) return fallbackText;

    // 静的オプション（divisionItems/selectionItems）から優先取得
    const staticOption = staticOptions.find((opt) => opt.value === value);
    if (staticOption?.label) {
      return staticOption.label;
    }

    // 動的オプションからフォールバック
    const dynamicOption = dynamicOptions.find((opt) => opt.value === value);
    if (dynamicOption?.label) {
      return dynamicOption.label;
    }

    return value;
  }, [previousValue, staticOptions, dynamicOptions, fallbackText]);
};

/**
 * 動的変更セレクトフィールドのロジック
 */
export const useDynamicChangeSelectLogic = (
  inputValue: FieldValueType | undefined,
  previousValue: FieldValueType | undefined,
  field: Attribute,
  options: Option[],
  onChange: (value: FieldValueType) => void,
) => {
  const { t } = useTranslation(defaultNS);
  const fallbackText = t("common.unregistered");

  // 現在の選択値を抽出
  const selectValue = useMemo(() => {
    return extractValueFromValueLabel(inputValue) ?? "";
  }, [inputValue]);

  // 前の値のラベルを取得
  const previousValueLabel = usePreviousValueLabel(
    previousValue,
    field,
    options,
  );

  // 読み取り専用時の表示値
  const readOnlyDisplayValue = useMemo(() => {
    if (!inputValue) return fallbackText;

    // ValueLabelObjectの場合はlabel部分を使用
    if (
      typeof inputValue === "object" &&
      inputValue !== null &&
      "label" in inputValue
    ) {
      return (inputValue as { label: string }).label;
    }

    return String(inputValue);
  }, [inputValue, fallbackText]);

  // 値変更ハンドラー
  const handleValueChange = useMemo(() => {
    return (newValue: string) => {
      if (newValue && newValue !== "") {
        // 選択されたオプションからValueLabelObjectを作成
        const selectedOption = options.find((opt) => opt.value === newValue);
        if (selectedOption?.label) {
          onChange({
            value: newValue,
            label: selectedOption.label,
            ...(selectedOption.description && {
              description: selectedOption.description,
            }),
          });
        } else {
          onChange(newValue);
        }
      } else {
        onChange(undefined);
      }
    };
  }, [options, onChange]);

  return {
    selectValue,
    previousValueLabel,
    readOnlyDisplayValue,
    handleValueChange,
  };
};
